import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { FieldSelectFit, FieldSelectFullWidth } from "@/components/trade-filter-selects";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { StrategyRdRuleComposer } from "../../../services/strategyRdApi";
import {
  strategyRdDeepMergePatch,
  strategyRdManageErrors,
  strategyRdManageForWire,
  type StrategyRdRuleFieldErrors,
} from "../../../utils/strategyRdRuleEditor";
import { StrategyRdManageTradeSection } from "./StrategyRdManageTradeSection";
import { StrategyRdMyRules } from "./StrategyRdMyRules";
import { toast } from "sonner";
import {
  StrategyRdEntryRuleEditor,
  StrategyRdFilterRuleEditor,
  StrategyRdPriceRuleEditor,
  strategyRdDefaultEntryRule,
  strategyRdDefaultFilterRule,
  strategyRdDefaultPriceRule,
  type StrategyRdRuleStatus,
} from "./StrategyRdRuleEditor";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import {
  defaultStrategyRdUnitSize,
  formatStrategyRdLeverage,
  resolveStrategyRdPipSize,
  strategyRdDefaultLeverage,
  strategyRdDefaultOffsetValue,
  strategyRdIsSideAwareStructuralAnchor,
  strategyRdSideAwarePickerValue,
  strategyRdStopOffsetNeedsValue,
  strategyRdStopOffsetShowsValueField,
  strategyRdUsesPipOrTick,
  withResolvedStrategyRdUnitSizes,
} from "../../../utils/strategyRd";
import type {
  StrategyRdAccountLimits,
  StrategyRdEntryRuleSpec,
  StrategyRdIntrabarMode,
  StrategyRdOffsetKind,
  StrategyRdRuleRole,
  StrategyRdRuleVocabulary,
  StrategyRdPositionMode,
  StrategyRdRuleTemplate,
  StrategyRdSizeMode,
  StrategyRdSizing,
  StrategyRdStopAnchor,
  StrategyRdStopDistanceBound,
  StrategyRdStopDistanceKind,
  StrategyRdTargetMode,
} from "../../../types/strategyRd";
import { StrategyRdSettingsSection, strategyRdSectionCardClassName } from "./strategyRdLabUi";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

const ANCHOR_OPTIONS: Array<{ value: StrategyRdStopAnchor; label: string }> = [
  { value: "signal_extreme", label: "Signal extreme" },
  { value: "signal_close", label: "Signal close" },
  { value: "level_extreme", label: "Level extreme" },
  { value: "prior_day", label: "Prior Day High or Low" },
  { value: "prior_week", label: "Prior Week High or Low" },
  { value: "prior_month", label: "Prior Month High or Low" },
  { value: "swing", label: "Swing High or Low" },
];

const OFFSET_OPTIONS: Array<{ value: StrategyRdOffsetKind; label: string }> = [
  { value: "none", label: "None" },
  { value: "beyond_bar", label: "Beyond bar" },
  { value: "beyond_level", label: "Beyond level" },
  { value: "atr_mult", label: "ATR ×" },
  { value: "pct_price", label: "% of price" },
  { value: "pips", label: "Pips" },
  { value: "ticks", label: "Ticks" },
];

const DISTANCE_KIND_OPTIONS: Array<{ value: StrategyRdStopDistanceKind; label: string }> = [
  { value: "price", label: "Price" },
  { value: "pips", label: "Pips" },
  { value: "ticks", label: "Ticks" },
  { value: "pct_price", label: "%" },
  { value: "atr_mult", label: "ATR" },
];

function stopWithDistanceBound(
  template: StrategyRdRuleTemplate,
  key: "min_distance" | "max_distance",
  next: StrategyRdStopDistanceBound | null
): StrategyRdRuleTemplate {
  const stop = { ...template.stop };
  if (next && next.value > 0) stop[key] = next;
  else delete stop[key];
  return { ...template, stop };
}

const TARGET_OPTIONS: Array<{ value: StrategyRdTargetMode; label: string }> = [
  { value: "r_multiple", label: "R multiple" },
  { value: "atr_mult", label: "ATR ×" },
  { value: "pct_price", label: "% of price" },
  { value: "pips", label: "Pips" },
  { value: "ticks", label: "Ticks" },
  { value: "prior_day", label: "Prior Day High or Low" },
  { value: "prior_week", label: "Prior Week High or Low" },
  { value: "prior_month", label: "Prior Month High or Low" },
  { value: "swing", label: "Swing High or Low" },
  { value: "level_extreme", label: "Level extreme" },
];

export function strategyRdTargetModeNeedsValue(mode: StrategyRdTargetMode | string): boolean {
  return (
    mode === "r_multiple" ||
    mode === "atr_mult" ||
    mode === "pct_price" ||
    mode === "pips" ||
    mode === "ticks"
  );
}

/**
 * Controlled number field that allows clearing / rewriting (empty mid-edit).
 * Keeps a local draft while focused so decimals like `0.25` are not snapped back to `0`.
 */
function NumericDraftInput({
  id,
  value,
  onCommit,
  min,
  max,
  step = "any",
}: {
  id: string;
  value: number;
  onCommit: (next: number) => void;
  min?: number;
  max?: number;
  step?: string | number;
}): JSX.Element {
  const [text, setText] = useState(() => String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [focused, value]);

  const clamp = (n: number): number => {
    let next = n;
    if (typeof min === "number" && next < min) next = min;
    if (typeof max === "number" && next > max) next = max;
    return next;
  };

  return (
    <Input
      id={id}
      type="number"
      min={min}
      max={max}
      step={step}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw.trim() === "" || raw === "-" || raw.endsWith(".") || raw === "-.") return;
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        onCommit(clamp(n));
      }}
      onBlur={() => {
        setFocused(false);
        const n = Number(text);
        if (text.trim() === "" || !Number.isFinite(n)) {
          setText(String(value));
          return;
        }
        const next = clamp(n);
        onCommit(next);
        setText(String(next));
      }}
    />
  );
}

/**
 * Optional positive unit size (pip/tick). Draft text so typing `0.01` keeps the leading `0`.
 * Empty / invalid commits `null` (BE/FE fall back to symbol default).
 */
function OptionalPositiveDraftInput({
  id,
  value,
  onCommit,
  placeholder,
  disabled = false,
}: {
  id: string;
  value: number | null;
  onCommit: (next: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
}): JSX.Element {
  const display = value != null && Number.isFinite(value) && value > 0 ? String(value) : "";
  const [text, setText] = useState(display);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(display);
  }, [display, focused]);

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      placeholder={placeholder}
      disabled={disabled}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
        setText(raw);
        if (raw.trim() === "" || raw === "." || raw.endsWith(".")) return;
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) return;
        onCommit(n);
      }}
      onBlur={() => {
        setFocused(false);
        const n = Number(text);
        if (text.trim() === "" || !Number.isFinite(n) || n <= 0) {
          onCommit(null);
          setText("");
          return;
        }
        onCommit(n);
        setText(String(n));
      }}
    />
  );
}

function StopDistanceField({
  idPrefix,
  label,
  bound,
  onCommit,
}: {
  idPrefix: string;
  label: string;
  bound: StrategyRdStopDistanceBound | null | undefined;
  onCommit: (next: StrategyRdStopDistanceBound | null) => void;
}): JSX.Element {
  const [kind, setKind] = useState<StrategyRdStopDistanceKind>(bound?.kind || "pips");
  const [on, setOn] = useState(Boolean(bound && bound.value > 0));
  const shownKind = bound?.kind || kind;
  const shownValue = bound && bound.value > 0 ? bound.value : null;

  useEffect(() => {
    if (bound && bound.value > 0) setOn(true);
  }, [bound]);

  return (
    <Field>
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{label}</FieldLabel>
      <div className="flex h-8 items-center gap-2">
        <Switch
          size="sm"
          checked={on}
          aria-label={`Turn ${label} on`}
          onCheckedChange={(checked) => {
            setOn(checked);
            if (!checked) onCommit(null);
          }}
        />
        <div className="min-w-0 flex-1">
          <OptionalPositiveDraftInput
            id={`${idPrefix}-value`}
            value={shownValue}
            placeholder={on ? "Value" : "Off"}
            disabled={!on}
            onCommit={(next) => {
              if (!on || next == null || !(next > 0)) onCommit(null);
              else onCommit({ kind: shownKind, value: next });
            }}
          />
        </div>
        <div className="shrink-0">
          <FieldSelectFit
            id={`${idPrefix}-kind`}
            value={shownKind}
            disabled={!on}
            fitToLongestOption
            onValueChange={(next) => {
              const unit = next as StrategyRdStopDistanceKind;
              setKind(unit);
              if (on && shownValue != null) onCommit({ kind: unit, value: shownValue });
            }}
            options={DISTANCE_KIND_OPTIONS}
          />
        </div>
      </div>
    </Field>
  );
}

const POSITION_MODE_OPTIONS: Array<{ value: StrategyRdPositionMode; label: string }> = [
  { value: "shared", label: "One account, longs and shorts together" },
  { value: "independent", label: "Longs and shorts as separate accounts" },
  { value: "combined", label: "One position at a time per direction (TradingView-style)" },
  { value: "one_at_a_time", label: "Never more than one open trade" },
];

const INTRABAR_OPTIONS: Array<{ value: StrategyRdIntrabarMode; label: string }> = [
  { value: "bar_path", label: "Like TradingView (bar path)" },
  { value: "bar_magnifier", label: "TradingView Bar Magnifier (Premium)" },
  { value: "stop_first", label: "Assume the stop (cautious; TradingView can't match)" },
];

const MAX_LEVERAGE = 1000;

/** Warning `action.field` → input id. Account & execution fields open that (collapsed) section first. */
const SETTING_FIELD_IDS: Record<string, { id: string; accountSection: boolean }> = {
  "sizing.limits.max_trades_per_day": { id: "strategy-rd-max-trades-day", accountSection: true },
  "sizing.limits.max_losses_per_day": { id: "strategy-rd-max-losses-day", accountSection: true },
  "sizing.value": { id: "strategy-rd-size-value", accountSection: false },
  "sizing.leverage": { id: "strategy-rd-leverage", accountSection: true },
  "sizing.position_mode": { id: "strategy-rd-position-mode", accountSection: true },
  "sizing.commission_pct": { id: "strategy-rd-commission", accountSection: false },
  "sizing.slippage_ticks": { id: "strategy-rd-slippage", accountSection: false },
  "sizing.spread_pct": { id: "strategy-rd-spread", accountSection: false },
  "sizing.swap_long_pct": { id: "strategy-rd-swap-long", accountSection: false },
  "sizing.swap_short_pct": { id: "strategy-rd-swap-short", accountSection: false },
  "template.intrabar": { id: "strategy-rd-intrabar", accountSection: true },
  "template.tick_size": { id: "strategy-rd-exec-tick-size", accountSection: true },
};

const SLIPPAGE_HELP =
  "Slippage is extra on top of the spread. Fills a little worse than the quote. In ticks of the " +
  "TradingView symbol. Commission is a % of each fill (stocks, crypto); FX and metals usually charge none.";

/** Set one daily limit (whole number ≥ 1, blank = none); null when both are blank. */
export function strategyRdLimitsWith(
  limits: StrategyRdAccountLimits | null | undefined,
  key: keyof StrategyRdAccountLimits,
  value: number | null
): StrategyRdAccountLimits | null {
  const next: StrategyRdAccountLimits = { ...(limits ?? {}) };
  const n = value == null ? null : Math.max(1, Math.min(1000, Math.floor(value)));
  if (n == null) delete next[key];
  else next[key] = n;
  return next.max_trades_per_day != null || next.max_losses_per_day != null ? next : null;
}

/** "10 ticks = 0.00010" from Apply's `tick_size_used`. */
export function strategyRdSlippageInPrice(
  ticks: number | null | undefined,
  tickSize: number | null | undefined
): string | null {
  if (ticks == null || !(ticks > 0) || tickSize == null || !(tickSize > 0)) return null;
  const decimals = Math.max(0, Math.min(10, Math.ceil(-Math.log10(tickSize) - 1e-9)));
  return `${ticks} tick${ticks === 1 ? "" : "s"} = ${(ticks * tickSize).toFixed(decimals)}`;
}

export type StrategyRdRulesPanelProps = {
  template: StrategyRdRuleTemplate;
  onChange: (next: StrategyRdRuleTemplate) => void;
  startingEquity: number;
  onStartingEquityChange: (next: number) => void;
  sizing: StrategyRdSizing;
  onSizingChange: (next: StrategyRdSizing) => void;
  onApply: () => void;
  applying: boolean;
  applyingLabel?: string | null;
  disabled?: boolean;
  disabledReason?: string | null;
  symbol?: string | null;
  assetClass?: string | null;
  /** Leverage the last backtest used (Apply echoes `sizing.leverage`). */
  appliedLeverage?: number | null;
  /** TradingView tick the last backtest used (`sizing.tick_size_used`). */
  appliedTickSize?: number | null;
  /** The instrument's typical spread / swap, offered in Trading costs. */
  typicalCosts?: Partial<StrategyRdSizing> | null;
  /** Warning action: open the setting and focus it (bump `token` to repeat). */
  focusRequest?: { field: string; token: number } | null;
  /** `POST /rules/vocabulary` for this recipe — fills every rule picker. */
  ruleVocabulary?: StrategyRdRuleVocabulary | null;
  /** The recipe rules are checked against; null = no setup yet (custom rules unavailable). */
  ruleComposer?: StrategyRdRuleComposer | null;
  /** Entry is set in the composer; a custom entry rule switches its mode to `rule`. */
  onEntryRuleChange?: (rule: StrategyRdEntryRuleSpec | null) => void;
  /** Apply / Save errors that belong to a rule field (400 invalid_template, 422 pine). */
  ruleFieldErrors?: StrategyRdRuleFieldErrors | null;
  /** Compile state of every active rule — a failing entry / filter also blocks Scan. */
  onRuleStatusesChange?: (statuses: Partial<Record<StrategyRdRuleRole, StrategyRdRuleStatus>>) => void;
};

/** Why Apply must wait, from the active rules' compile state; null = ready. */
export function strategyRdRulesBlockingReason(
  statuses: Partial<Record<StrategyRdRuleRole, StrategyRdRuleStatus>>
): string | null {
  const active = Object.values(statuses).filter(
    (s): s is StrategyRdRuleStatus => s != null && s.state !== "idle"
  );
  if (active.some((s) => s.state === "error")) return "Fix the custom rule marked in red first.";
  if (active.some((s) => s.state === "checking")) return "Checking custom rules…";
  return null;
}

/**
 * "Preset / Custom rule" — Preset is the menus; Custom shows the sentence builder. A line under
 * the switch says what the current choice does, so the two options explain themselves.
 */
function PresetCustomSwitch({
  label,
  custom,
  onCustomChange,
  presetLabel = "Preset",
  presetHint,
  customHint,
  disabled = false,
}: {
  label: string;
  custom: boolean;
  onCustomChange: (custom: boolean) => void;
  presetLabel?: string;
  /** What the preset does, e.g. "Uses the stop from your recipe's setup." */
  presetHint?: string;
  /** What a custom rule lets you do, with an example. */
  customHint?: string;
  disabled?: boolean;
}): JSX.Element {
  const hint = custom ? customHint : presetHint;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{label}</span>
        <Tabs data-trade-log-tabs="" value={custom ? "custom" : "preset"} onValueChange={(v) => onCustomChange(v === "custom")}>
          <TabsList className="h-7" aria-label={`${label}: preset or custom rule`}>
            <TabsTrigger value="preset" className="h-6 px-2 text-xs" title={presetHint}>
              {presetLabel}
            </TabsTrigger>
            <TabsTrigger value="custom" className="h-6 px-2 text-xs" disabled={disabled} title={customHint}>
              Custom rule
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {hint ? <p className={`${strategyRdMetaClassName} max-w-[22rem] text-[11px]`}>{hint}</p> : null}
    </div>
  );
}

const SIZE_MODE_OPTIONS: Array<{ value: StrategyRdSizeMode; label: string }> = [
  { value: "risk_pct", label: "Risk % of equity" },
  { value: "fixed_qty", label: "Fixed qty" },
];

export function StrategyRdRulesPanel({
  template,
  onChange,
  startingEquity,
  onStartingEquityChange,
  sizing,
  onSizingChange,
  onApply,
  applying,
  applyingLabel = null,
  disabled = false,
  disabledReason,
  symbol,
  assetClass,
  appliedLeverage = null,
  appliedTickSize = null,
  typicalCosts = null,
  focusRequest = null,
  ruleVocabulary = null,
  ruleComposer = null,
  onEntryRuleChange,
  ruleFieldErrors = null,
  onRuleStatusesChange,
}: StrategyRdRulesPanelProps): JSX.Element {
  const [accountOpen, setAccountOpen] = useState(false);
  const [ruleStatuses, setRuleStatuses] = useState<
    Partial<Record<StrategyRdRuleRole, StrategyRdRuleStatus>>
  >({});
  const statusSetters = useMemo(() => {
    const make = (role: StrategyRdRuleRole) => (status: StrategyRdRuleStatus) =>
      setRuleStatuses((prev) => (prev[role] === status ? prev : { ...prev, [role]: status }));
    return { stop: make("stop"), target: make("target"), entry: make("entry"), filter: make("filter") };
  }, []);
  const stopRule = template.stop.rule ?? null;
  const targetRule = template.target.rule ?? null;
  const entryRule = template.entry.mode === "rule" ? template.entry.rule ?? null : null;
  const filterRule = template.entry.filter ?? null;
  // A switched-off rule has no editor to report "idle" — drop its last state here.
  const activeStatuses = useMemo(() => {
    const out: Partial<Record<StrategyRdRuleRole, StrategyRdRuleStatus>> = {};
    if (stopRule && ruleStatuses.stop) out.stop = ruleStatuses.stop;
    if (targetRule && ruleStatuses.target) out.target = ruleStatuses.target;
    if (entryRule && ruleStatuses.entry) out.entry = ruleStatuses.entry;
    if (filterRule && ruleStatuses.filter) out.filter = ruleStatuses.filter;
    return out;
  }, [entryRule, filterRule, ruleStatuses, stopRule, targetRule]);
  useEffect(() => {
    onRuleStatusesChange?.(activeStatuses);
  }, [activeStatuses, onRuleStatusesChange]);
  // Closed (drawer shut): keep real errors, drop "checking" — nothing is left to finish it.
  const activeStatusesRef = useRef(activeStatuses);
  activeStatusesRef.current = activeStatuses;
  const onRuleStatusesChangeRef = useRef(onRuleStatusesChange);
  onRuleStatusesChangeRef.current = onRuleStatusesChange;
  useEffect(
    () => () => {
      const kept = Object.fromEntries(
        Object.entries(activeStatusesRef.current).filter(([, st]) => st?.state === "error")
      );
      onRuleStatusesChangeRef.current?.(kept);
    },
    []
  );
  const rulesBlocking = strategyRdRulesBlockingReason(activeStatuses);
  const manageInvalid = Object.keys(strategyRdManageErrors(template.manage)).length > 0;
  const customUnavailable = !ruleComposer || !ruleVocabulary;
  const setStopRule = useCallback(
    (rule: StrategyRdRuleTemplate["stop"]["rule"]) => {
      const { rule: _drop, ...stop } = template.stop;
      onChange({ ...template, stop: rule ? { ...stop, rule } : stop });
    },
    [onChange, template]
  );
  const setTargetRule = useCallback(
    (rule: StrategyRdRuleTemplate["target"]["rule"]) => {
      const { rule: _drop, ...target } = template.target;
      onChange({ ...template, target: rule ? { ...target, rule } : target });
    },
    [onChange, template]
  );
  const setFilterRule = useCallback(
    (filter: StrategyRdRuleTemplate["entry"]["filter"]) => {
      const { filter: _drop, ...entry } = template.entry;
      onChange({ ...template, entry: filter ? { ...entry, filter } : entry });
    },
    [onChange, template]
  );
  /** A saved rule fitted to this recipe: merge its patch; the editor opens and reads it back. */
  const applySavedRule = useCallback(
    (patch: Record<string, unknown>, readback: string | null) => {
      const entry = patch.entry && typeof patch.entry === "object" ? (patch.entry as Record<string, unknown>) : null;
      if (entry?.mode === "rule" && entry.rule && typeof entry.rule === "object") {
        onEntryRuleChange?.(entry.rule as StrategyRdEntryRuleSpec);
      } else {
        onChange(strategyRdDeepMergePatch(template, patch));
      }
      if (readback) toast.message("Rule applied", { description: readback });
    },
    [onChange, onEntryRuleChange, template]
  );
  const myRulesProps = { composer: ruleComposer, vocabulary: ruleVocabulary, onApply: applySavedRule };
  useEffect(() => {
    if (!focusRequest) return;
    const target = SETTING_FIELD_IDS[focusRequest.field];
    if (!target) return;
    if (target.accountSection) setAccountOpen(true);
    // After the section opens.
    const raf = window.requestAnimationFrame(() => {
      const el = document.getElementById(target.id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [focusRequest]);
  const showOffsetValue = strategyRdStopOffsetShowsValueField(template.stop.offset.kind);
  const showUnits = strategyRdUsesPipOrTick(template);
  const unitOpts = { symbol, assetClass };
  const defaultUnit = defaultStrategyRdUnitSize(unitOpts);
  const pipPlaceholder = String(resolveStrategyRdPipSize(template, unitOpts));
  const sizingInvalid =
    !(startingEquity > 0) ||
    !(sizing.value > 0) ||
    (sizing.mode !== "risk_pct" && sizing.mode !== "fixed_qty");

  const sizeValueLabel = sizing.mode === "risk_pct" ? "Risk % of equity" : "Size qty";

  const commitTemplate = (next: StrategyRdRuleTemplate) => {
    onChange(withResolvedStrategyRdUnitSizes(next, unitOpts));
  };

  return (
    <div className="flex flex-col gap-4" data-strategy-rd-rules="">
      <StrategyRdSettingsSection
        step={1}
        title="Exit"
        description="Where each trade's stop and target sit. Preset reads the recipe's levels; Custom builds your own rule. An indicator, a level or a distance."
      >

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="inline-flex flex-wrap items-start gap-1">
          <PresetCustomSwitch
            label="Stop"
            presetHint="Uses the stop from your recipe's setup. E.g. just beyond the signal candle."
            customHint="Write your own as a sentence. E.g. 0.5 ATR below the swing low."
            custom={Boolean(stopRule)}
            disabled={customUnavailable}
            onCustomChange={(custom) =>
              setStopRule(custom ? strategyRdDefaultPriceRule(ruleVocabulary, "stop") : null)
            }
          />
          <StrategyRdMyRules role="stop" currentRule={stopRule} {...myRulesProps} />
        </span>
        <span className="inline-flex flex-wrap items-start gap-1">
          <PresetCustomSwitch
            label="Target"
            presetHint="A fixed reward: the R multiple set below (2R = twice the risk)."
            customHint="Write your own as a sentence. E.g. at the previous day's high."
            custom={Boolean(targetRule)}
            disabled={customUnavailable}
            onCustomChange={(custom) =>
              setTargetRule(custom ? strategyRdDefaultPriceRule(ruleVocabulary, "target") : null)
            }
          />
          <StrategyRdMyRules role="target" currentRule={targetRule} {...myRulesProps} />
        </span>
      </div>

      {stopRule ? (
        <div className="flex flex-col gap-1">
          <span className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Stop at</span>
          <StrategyRdPriceRuleEditor
            role="stop"
            rule={stopRule}
            onChange={setStopRule}
            vocabulary={ruleVocabulary}
            composer={ruleComposer}
            onStatusChange={statusSetters.stop}
          />
          {ruleFieldErrors?.["stop.rule"] ? (
            <p className="text-xs text-destructive">{ruleFieldErrors["stop.rule"]}</p>
          ) : null}
        </div>
      ) : null}
      {targetRule ? (
        <div className="flex flex-col gap-1">
          <span className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Target at</span>
          <StrategyRdPriceRuleEditor
            role="target"
            rule={targetRule}
            onChange={setTargetRule}
            vocabulary={ruleVocabulary}
            composer={ruleComposer}
            onStatusChange={statusSetters.target}
          />
          {ruleFieldErrors?.["target.rule"] ? (
            <p className="text-xs text-destructive">{ruleFieldErrors["target.rule"]}</p>
          ) : null}
        </div>
      ) : null}

      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-4">
        {stopRule ? null : (
        <>
        <Field>
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Stop anchor</FieldLabel>
          <FieldSelectFullWidth
            id="strategy-rd-stop-anchor"
            value={strategyRdSideAwarePickerValue(template.stop.anchor)}
            onValueChange={(next) =>
              onChange({
                ...template,
                stop: { ...template.stop, anchor: next as StrategyRdStopAnchor },
              })
            }
            options={ANCHOR_OPTIONS}
          />
          {strategyRdIsSideAwareStructuralAnchor(
            strategyRdSideAwarePickerValue(template.stop.anchor)
          ) ? (
            <p className={`${strategyRdMetaClassName} mt-1 text-[11px]`}>
              Trade direction picks the side. Long → low · short → high.
            </p>
          ) : null}
        </Field>

        <Field>
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Stop offset</FieldLabel>
          <FieldSelectFullWidth
            id="strategy-rd-stop-offset"
            value={template.stop.offset.kind}
            onValueChange={(next) => {
              const kind = next as StrategyRdOffsetKind;
              const keepValue = strategyRdStopOffsetNeedsValue(template.stop.offset.kind);
              const value = strategyRdStopOffsetNeedsValue(kind)
                ? keepValue
                  ? template.stop.offset.value
                  : strategyRdDefaultOffsetValue(kind)
                : kind === "beyond_bar" || kind === "beyond_level"
                  ? template.stop.offset.value
                  : 0;
              commitTemplate({
                ...template,
                stop: { ...template.stop, offset: { kind, value } },
              });
            }}
            options={OFFSET_OPTIONS}
          />
        </Field>

        {showOffsetValue ? (
          <Field>
            <FieldLabel htmlFor="strategy-rd-stop-value" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              {template.stop.offset.kind === "pips"
                ? "Stop pips"
                : template.stop.offset.kind === "ticks"
                  ? "Stop ticks"
                  : template.stop.offset.kind === "atr_mult"
                    ? "ATR multiple"
                    : template.stop.offset.kind === "pct_price"
                      ? "% of price"
                      : template.stop.offset.kind === "beyond_level"
                        ? "Beyond level buffer"
                        : template.stop.offset.kind === "beyond_bar"
                          ? "Beyond bar buffer"
                          : "Stop value"}
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-stop-value"
              value={template.stop.offset.value}
              min={0}
              onCommit={(next) =>
                onChange({
                  ...template,
                  stop: {
                    ...template.stop,
                    offset: { ...template.stop.offset, value: next },
                  },
                })
              }
            />
          </Field>
        ) : null}
        </>
        )}

        <StopDistanceField
          idPrefix="strategy-rd-stop-min"
          label="Min stop"
          bound={template.stop.min_distance}
          onCommit={(next) => onChange(stopWithDistanceBound(template, "min_distance", next))}
        />
        <StopDistanceField
          idPrefix="strategy-rd-stop-max"
          label="Max stop"
          bound={template.stop.max_distance}
          onCommit={(next) => onChange(stopWithDistanceBound(template, "max_distance", next))}
        />

        {targetRule ? null : (
        <>
        <Field>
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Target</FieldLabel>
          <FieldSelectFullWidth
            id="strategy-rd-target-mode"
            value={strategyRdSideAwarePickerValue(template.target.mode)}
            onValueChange={(next) =>
              commitTemplate({
                ...template,
                target: {
                  ...template.target,
                  mode: next as StrategyRdTargetMode,
                  value: strategyRdTargetModeNeedsValue(next)
                    ? template.target.value > 0
                      ? template.target.value
                      : next === "r_multiple"
                        ? 2
                        : 1
                    : 1,
                },
              })
            }
            options={TARGET_OPTIONS}
          />
          {strategyRdIsSideAwareStructuralAnchor(
            strategyRdSideAwarePickerValue(template.target.mode)
          ) ? (
            <p className={`${strategyRdMetaClassName} mt-1 text-[11px]`}>
              Trade direction picks the side. Long → high · short → low.
            </p>
          ) : null}
        </Field>

        {strategyRdTargetModeNeedsValue(template.target.mode) ? (
          <Field>
            <FieldLabel htmlFor="strategy-rd-target-value" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Target value
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-target-value"
              value={template.target.value}
              min={0}
              onCommit={(next) =>
                onChange({
                  ...template,
                  target: { ...template.target, value: next },
                })
              }
            />
          </Field>
        ) : null}
        </>
        )}

        <Field>
          <FieldLabel htmlFor="strategy-rd-atr-length" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
            ATR length
          </FieldLabel>
          <NumericDraftInput
            id="strategy-rd-atr-length"
            value={template.atr_length}
            min={2}
            max={100}
            step={1}
            onCommit={(next) =>
              onChange({
                ...template,
                atr_length: Math.max(2, Math.min(100, Math.floor(next))),
              })
            }
          />
        </Field>

        {showUnits ? (
          <>
            <Field>
              <FieldLabel htmlFor="strategy-rd-pip-size" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                Pip size
              </FieldLabel>
              <OptionalPositiveDraftInput
                id="strategy-rd-pip-size"
                value={template.pip_size ?? null}
                placeholder={pipPlaceholder}
                onCommit={(next) =>
                  onChange({
                    ...template,
                    pip_size: next,
                  })
                }
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="strategy-rd-tick-size" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                Tick size
              </FieldLabel>
              <OptionalPositiveDraftInput
                id="strategy-rd-tick-size"
                value={template.tick_size ?? null}
                placeholder={String(defaultUnit)}
                onCommit={(next) =>
                  onChange({
                    ...template,
                    tick_size: next,
                  })
                }
              />
            </Field>
          </>
        ) : null}
      </div>

      </StrategyRdSettingsSection>

      <StrategyRdSettingsSection
        step={2}
        title="Entry"
        description="How a trade opens once the setup fires, and an optional filter that drops setups before they count."
      >
      <div className="flex flex-col gap-2" data-strategy-rd-entry-rule-section="">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="inline-flex flex-wrap items-start gap-1">
            <PresetCustomSwitch
              label="Entry"
              presetLabel="From the composer"
              presetHint="Enters the way your recipe says. E.g. at the close of the signal candle."
              customHint="Choose the order and when it fills. E.g. a limit order at the middle of the zone."
              custom={Boolean(entryRule)}
              disabled={customUnavailable || !onEntryRuleChange}
              onCustomChange={(custom) =>
                onEntryRuleChange?.(custom ? strategyRdDefaultEntryRule(ruleVocabulary) : null)
              }
            />
            <StrategyRdMyRules
              role="entry"
              currentRule={entryRule as Record<string, unknown> | null}
              {...myRulesProps}
            />
          </span>
          <span className="inline-flex flex-wrap items-start gap-1">
            <PresetCustomSwitch
              label="Setup filter"
              presetLabel="None"
              presetHint="Every setup the recipe finds is traded."
              customHint="Skip setups that don't meet a condition. E.g. only when price is above the 200 EMA."
              custom={Boolean(filterRule)}
              disabled={customUnavailable}
              onCustomChange={(custom) =>
                setFilterRule(custom ? strategyRdDefaultFilterRule(ruleVocabulary) : null)
              }
            />
            <StrategyRdMyRules role="filter" currentRule={filterRule} {...myRulesProps} />
          </span>
        </div>
        {customUnavailable ? (
          <p className={`${strategyRdMetaClassName} text-[11px]`}>
            Custom rules read the recipe&apos;s prices. Pick a setup first.
          </p>
        ) : null}
        {entryRule ? (
          <div className="flex flex-col gap-1">
            <p className={`${strategyRdMetaClassName} text-[11px]`}>
              Checked at each entry-timeframe close after the setup, within Max wait. Zones and
              levels of the recipe are context here. The rule decides the fill. Changing it needs
              a new scan.
            </p>
            <StrategyRdEntryRuleEditor
              rule={entryRule}
              onChange={(next) => onEntryRuleChange?.(next)}
              vocabulary={ruleVocabulary}
              composer={ruleComposer}
              onStatusChange={statusSetters.entry}
            />
            {ruleFieldErrors?.["entry.rule"] ? (
              <p className="text-xs text-destructive">{ruleFieldErrors["entry.rule"]}</p>
            ) : null}
          </div>
        ) : null}
        {filterRule ? (
          <div className="flex flex-col gap-1">
            <p className={`${strategyRdMetaClassName} text-[11px]`}>
              Checked once, when the setup and its confluences are confirmed. Setups it drops are
              not hits. Changing it needs a new scan.
            </p>
            <StrategyRdFilterRuleEditor
              rule={filterRule}
              onChange={setFilterRule}
              vocabulary={ruleVocabulary}
              composer={ruleComposer}
              onStatusChange={statusSetters.filter}
            />
            {ruleFieldErrors?.["entry.filter"] ? (
              <p className="text-xs text-destructive">{ruleFieldErrors["entry.filter"]}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      </StrategyRdSettingsSection>

      <StrategyRdManageTradeSection
        step={3}
        manage={template.manage}
        onChange={(next) => {
          const { manage: _drop, ...rest } = template;
          const manage = strategyRdManageForWire(next);
          onChange(manage ? { ...rest, manage } : rest);
        }}
        serverError={ruleFieldErrors?.manage ?? null}
        myRules={
          <StrategyRdMyRules
            role="manage"
            currentRule={(template.manage as Record<string, unknown> | null | undefined) ?? null}
            {...myRulesProps}
          />
        }
      />

      <StrategyRdSettingsSection
        step={4}
        title="Size"
        description="The account the backtest starts with, how big each trade is and the leverage your broker gives."
      >
      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4">
        <Field>
          <FieldLabel htmlFor="strategy-rd-starting-equity" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
            Starting equity
          </FieldLabel>
          <NumericDraftInput
            id="strategy-rd-starting-equity"
            value={startingEquity}
            min={1}
            onCommit={onStartingEquityChange}
          />
        </Field>
        <Field>
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Size mode</FieldLabel>
          <FieldSelectFullWidth
            id="strategy-rd-size-mode"
            value={sizing.mode}
            onValueChange={(next) => {
              const mode = next as StrategyRdSizeMode;
              const keep =
                sizing.value > 0
                  ? sizing.value
                  : mode === "fixed_qty"
                    ? 1
                    : 1;
              onSizingChange({ ...sizing, mode, value: keep });
            }}
            options={SIZE_MODE_OPTIONS}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="strategy-rd-size-value" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
            {sizeValueLabel}
          </FieldLabel>
          <NumericDraftInput
            id="strategy-rd-size-value"
            value={sizing.value}
            min={0}
            onCommit={(next) => onSizingChange({ ...sizing, value: next })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="strategy-rd-leverage" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
            Account leverage
          </FieldLabel>
          <OptionalPositiveDraftInput
            id="strategy-rd-leverage"
            value={sizing.leverage ?? null}
            placeholder={`${strategyRdDefaultLeverage(unitOpts)}× (default)`}
            onCommit={(next) =>
              onSizingChange({ ...sizing, leverage: next == null ? null : Math.min(next, MAX_LEVERAGE) })
            }
          />
          {appliedLeverage ? (
            <span className="text-[11px] text-muted-foreground">
              Last backtest used {formatStrategyRdLeverage(appliedLeverage)}
            </span>
          ) : null}
        </Field>
      </div>

      </StrategyRdSettingsSection>

      <StrategyRdSettingsSection
        step={5}
        title="Trading costs"
        description="Lab prices are mid prices, so every fill pays half the spread. Swap is charged for each 17:00 New York rollover a trade is held through (three on Wednesday). Use your broker's rates; positive means you pay."
        data-strategy-rd-costs-section=""
      >
        <CostFields
          sizing={sizing}
          onSizingChange={onSizingChange}
          appliedTickSize={appliedTickSize}
          typicalCosts={typicalCosts}
        />
      </StrategyRdSettingsSection>

      <AccountExecutionSection
        template={template}
        onChange={onChange}
        sizing={sizing}
        onSizingChange={onSizingChange}
        showTickSize={!showUnits}
        defaultLeverage={strategyRdDefaultLeverage(unitOpts)}
        appliedLeverage={appliedLeverage}
        appliedTickSize={appliedTickSize}
        open={accountOpen}
        onOpenChange={setAccountOpen}
      />

      <div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex flex-wrap items-center gap-3 !border-0 !border-t !border-solid !border-border bg-background/95 px-5 py-3 backdrop-blur">
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          data-strategy-rd-apply=""
          onClick={onApply}
          disabled={disabled || applying || sizingInvalid || Boolean(rulesBlocking) || manageInvalid}
        >
          {applying ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          <span className="normal-case tracking-normal">
            {applying ? applyingLabel || "Running backtest…" : "Run backtest"}
          </span>
        </Button>
        {disabledReason || sizingInvalid || rulesBlocking || manageInvalid ? (
          <span className={strategyRdMetaClassName}>
            {sizingInvalid
              ? "Set starting equity and a positive size before running the backtest."
              : rulesBlocking ||
                (manageInvalid ? "Fix Manage the trade first." : null) ||
                disabledReason}
          </span>
        ) : null}
      </div>
      {ruleFieldErrors?.general ? (
        <p className="text-xs text-destructive" data-strategy-rd-rule-error-general="">
          {ruleFieldErrors.general}
        </p>
      ) : null}
    </div>
  );
}

/** Spread, overnight swap, commission and slippage — always visible, so costs are easy to find. */
function CostFields({
  sizing,
  onSizingChange,
  appliedTickSize,
  typicalCosts = null,
}: {
  sizing: StrategyRdSizing;
  onSizingChange: (next: StrategyRdSizing) => void;
  appliedTickSize: number | null;
  /** The instrument's typical spread / swap (from the last backtest), one click to fill. */
  typicalCosts?: Partial<StrategyRdSizing> | null;
}): JSX.Element {
  const slippageInPrice = strategyRdSlippageInPrice(sizing.slippage_ticks, appliedTickSize);
  const typical = typicalCosts && Object.values(typicalCosts).some((v) => typeof v === "number" && v !== 0) ? typicalCosts : null;
  const matchesTypical =
    typical != null &&
    (["spread_pct", "swap_long_pct", "swap_short_pct"] as const).every(
      (k) => (typical[k] ?? 0) === (sizing[k] ?? 0)
    );
  return (
    <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-4">
          {typical ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-border bg-muted/30 px-3 py-2.5 sm:col-span-2">
              <span className="font-[family-name:var(--font-secondary)] text-xs text-muted-foreground">
                Typical for this instrument: spread {typical.spread_pct ?? 0}% · swap {typical.swap_long_pct ?? 0}% long /{" "}
                {typical.swap_short_pct ?? 0}% short per night
              </span>
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                data-font="primary"
                className="h-7 px-2.5 shadow-none"
                disabled={matchesTypical}
                onClick={() => onSizingChange({ ...sizing, ...typical })}
                data-strategy-rd-fill-typical-costs=""
              >
                <span className="normal-case tracking-normal">{matchesTypical ? "Using typical costs" : "Fill typical costs"}</span>
              </Button>
            </div>
          ) : null}
          <Field>
            <FieldLabel htmlFor="strategy-rd-spread" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Spread (% of price)
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-spread"
              value={sizing.spread_pct ?? 0}
              min={0}
              max={5}
              step={0.0005}
              onCommit={(next) => onSizingChange({ ...sizing, spread_pct: next })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-swap-long" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Swap per night, longs (%)
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-swap-long"
              value={sizing.swap_long_pct ?? 0}
              min={-1}
              max={1}
              step={0.001}
              onCommit={(next) => onSizingChange({ ...sizing, swap_long_pct: next })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-swap-short" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Swap per night, shorts (%)
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-swap-short"
              value={sizing.swap_short_pct ?? 0}
              min={-1}
              max={1}
              step={0.001}
              onCommit={(next) => onSizingChange({ ...sizing, swap_short_pct: next })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-commission" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Commission % per fill
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-commission"
              value={sizing.commission_pct ?? 0}
              min={0}
              max={5}
              onCommit={(next) => onSizingChange({ ...sizing, commission_pct: next })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-slippage" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Slippage (ticks)
            </FieldLabel>
            <NumericDraftInput
              id="strategy-rd-slippage"
              value={sizing.slippage_ticks ?? 0}
              min={0}
              max={1000}
              step={1}
              onCommit={(next) => onSizingChange({ ...sizing, slippage_ticks: Math.floor(next) })}
            />
            {slippageInPrice ? (
              <p className={`${strategyRdMetaClassName} mt-1 text-[11px]`} data-strategy-rd-slippage-price="">
                {slippageInPrice}
              </p>
            ) : null}
          </Field>
          <p className={`${strategyRdMetaClassName} text-[11px] sm:col-span-2`}>{SLIPPAGE_HELP}</p>
    </div>
  );
}

/** Leverage, position mode, daily limits and same-bar fills — collapsed; defaults match TradingView. */
function AccountExecutionSection({
  template,
  onChange,
  sizing,
  onSizingChange,
  showTickSize,
  defaultLeverage,
  appliedLeverage,
  appliedTickSize,
  open,
  onOpenChange,
}: {
  template: StrategyRdRuleTemplate;
  onChange: (next: StrategyRdRuleTemplate) => void;
  sizing: StrategyRdSizing;
  onSizingChange: (next: StrategyRdSizing) => void;
  /** Tick size already sits with the pip/tick units when those are in use. */
  showTickSize: boolean;
  defaultLeverage: number;
  appliedLeverage: number | null;
  appliedTickSize: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const appliedLabel = formatStrategyRdLeverage(appliedLeverage);

  return (
    // The same settings card as the other sections; its body folds away.
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      className={strategyRdSectionCardClassName}
      data-strategy-rd-account-execution=""
    >
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-start justify-between gap-3 text-left">
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
              Account &amp; execution
            </span>
            <span className="font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">
              Longs and shorts, daily limits and same-bar fills. Defaults match TradingView.
            </span>
          </span>
          <ChevronDown
            className={`mt-0.5 size-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3">
        <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-4">
          <Field className="sm:col-span-2">
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Longs and shorts</FieldLabel>
            <FieldSelectFullWidth
              id="strategy-rd-position-mode"
              value={sizing.position_mode || "shared"}
              onValueChange={(next) =>
                onSizingChange({ ...sizing, position_mode: next as StrategyRdPositionMode })
              }
              options={POSITION_MODE_OPTIONS}
            />
            <p className={`${strategyRdMetaClassName} mt-1 text-[11px]`}>
              Separately: each side has its own account, as two TradingView scripts. One account:
              like one TradingView script. While a long is open, shorts are skipped. One at a
              time: never more than one open trade.
            </p>
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-max-trades-day" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Max trades per day
            </FieldLabel>
            <OptionalPositiveDraftInput
              id="strategy-rd-max-trades-day"
              value={sizing.limits?.max_trades_per_day ?? null}
              placeholder="No limit"
              onCommit={(next) =>
                onSizingChange({
                  ...sizing,
                  limits: strategyRdLimitsWith(sizing.limits, "max_trades_per_day", next),
                })
              }
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="strategy-rd-max-losses-day" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Stop for the day after N losing trades
            </FieldLabel>
            <OptionalPositiveDraftInput
              id="strategy-rd-max-losses-day"
              value={sizing.limits?.max_losses_per_day ?? null}
              placeholder="No limit"
              onCommit={(next) =>
                onSizingChange({
                  ...sizing,
                  limits: strategyRdLimitsWith(sizing.limits, "max_losses_per_day", next),
                })
              }
            />
          </Field>
          <p className={`${strategyRdMetaClassName} text-[11px] sm:col-span-2`}>
            Days are UTC. Later signals that day are skipped. A loss counts once the whole trade
            (partials included) closed below zero.
          </p>
          <Field className="sm:col-span-2">
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Stop and target on the same bar
            </FieldLabel>
            <FieldSelectFullWidth
              id="strategy-rd-intrabar"
              value={template.intrabar || "bar_path"}
              onValueChange={(next) =>
                onChange({ ...template, intrabar: next as StrategyRdIntrabarMode })
              }
              options={INTRABAR_OPTIONS}
            />
          </Field>
          {showTickSize ? (
            <Field>
              <FieldLabel htmlFor="strategy-rd-exec-tick-size" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                Tick size
              </FieldLabel>
              <OptionalPositiveDraftInput
                id="strategy-rd-exec-tick-size"
                value={template.tick_size ?? null}
                placeholder="From the data"
                onCommit={(next) => onChange({ ...template, tick_size: next })}
              />
            </Field>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
